import { ReactElement, useEffect, useState } from 'react';
import {
  RadioGroup,
  Checkbox,
  CheckboxGroup,
  Button,
  HStack,
  Box,
  Progress,
  VStack,
  Text,
  Badge,
  Collapsible,
} from '@chakra-ui/react';
import { NovaIcon } from 'components/ui/novaIcon';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { AiFileIcon, ChevronDownIcon, ChevronRightIcon } from 'assets/icons/v2';
import { useAiWorksheetGeneration } from 'hooks/api/backend/worksheets/useAiWorksheetGeneration';
import { useNavigate } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';
import { useUser } from 'utils/auth';
import { useGetWorksheetsByArticle } from 'hooks/api/backend/worksheets/useWorksheets';
import {
  KnowledgeDimension,
  BloomCategory,
  WorksheetAiModel,
  toGermanLabel,
} from 'types/Worksheets';
import { NoTokensAlert } from 'components/ai-subscription/NoTokensAlert';
import { useTokenAccount } from 'hooks/api/backend/ai-subscription/useTokenAccount';
import { AiTierLevel } from 'types/AiSubscription';
import { SubscriptionStatus } from '@novaheal/types';
import { useOptimisticTokenUpdate } from 'hooks/useOptimisticTokenUpdate';
import { AiSubscriptionTokenBadge } from 'components/ai-subscription/AiSubscriptionTokenBadge';
import { useIsTeacherOrAdmin } from 'hooks/utils/useIsTeacherOrAdmin';

type Props = {
  articleId: string;
};

export const WorksheetGeneratorSegment = ({ articleId }: Props): ReactElement | null => {
  const navigate = useNavigate();
  const { data: user } = useUser();
  const isTeacherOrAdmin = useIsTeacherOrAdmin();

  const { data: tokenAccount, refetch: refetchTokenAccount } = useTokenAccount();
  const { optimisticallyConsumeTokens, revertOptimisticUpdate } = useOptimisticTokenUpdate(
    tokenAccount,
    user?.subscription?.status
  );

  const [isOpen, setIsOpen] = useState(false);
  const [userToggled, setUserToggled] = useState(false);
  const [model, setModel] = useState<WorksheetAiModel>(WorksheetAiModel.GENERATIVE_AI);
  const [difficulty, setDifficulty] = useState<BloomCategory>(BloomCategory.EASY);
  const [knowledgeDimensions, setKnowledgeDimensions] = useState<KnowledgeDimension[]>([
    KnowledgeDimension.FACTUAL_KNOWLEDGE,
    KnowledgeDimension.CONCEPTUAL_KNOWLEDGE,
    KnowledgeDimension.PROCEDURAL_KNOWLEDGE,
  ]);
  const [useSimpleLanguage, setUseSimpleLanguage] = useState<boolean>(false);

  const {
    generateWorksheet,
    worksheet,
    isLoading,
    isPolling,
    progress,
    progressMessage,
    error,
    reset,
  } = useAiWorksheetGeneration(articleId);

  const { data: relatedWorksheets } = useGetWorksheetsByArticle(articleId);

  // Colors (must be called unconditionally before any early return)

  // Navigate to worksheet editor when worksheet is generated
  useEffect(() => {
    if (worksheet) {
      navigate(`${ProtectedRoutes.WORKSHEET_DETAIL}/${worksheet.id}`);
    }
  }, [worksheet, navigate]);

  const handleCreateWorksheet = (): void => {
    // Pass difficulty and knowledgeDimensions to the generation function
    if (model === WorksheetAiModel.BLOOM_METHODOLOGY) {
      generateWorksheet(articleId, {
        model,
        variables: { bloomCategory: difficulty, knowledgeDimensions },
        useSimpleLanguage,
      });
    } else {
      generateWorksheet(articleId, { model, useSimpleLanguage });
    }

    // Optimistically update token count immediately
    optimisticallyConsumeTokens(1);

    // Refetch after a delay to ensure we have the correct server state
    setTimeout(() => {
      refetchTokenAccount();
    }, 3000);
  };

  // Auto-open when there is activity or an error, unless user manually toggled
  useEffect(() => {
    if (!userToggled && (isLoading || isPolling || !!error)) {
      setIsOpen(true);
    }
  }, [isLoading, isPolling, error, userToggled]);

  // Revert optimistic update if worksheet generation fails
  useEffect(() => {
    if (error) {
      // Revert the optimistic update
      revertOptimisticUpdate();
    }
  }, [error, revertOptimisticUpdate]);

  const handleUpgradeClick = (): void => {
    navigate(ProtectedRoutes.AI_SUBSCRIPTION_MANAGEMENT);
  };

  if (!isTeacherOrAdmin) {
    return null;
  }

  return (
    <Box
      width="100%"
      borderLeftWidth="4px"
      borderColor="border.accent.active"
      bg="tag.background.green"
      borderRadius="md"
      p={{ base: 'm', md: 'l' }}
      mb={{ base: 'l', md: '30px' }}
    >
      <VStack align="stretch" gap="s" width="100%">
        <HStack justify="space-between" align="center">
          <HStack
            gap="s"
            flex={1}
            cursor="pointer"
            onClick={() => {
              setUserToggled(true);
              setIsOpen((prev) => !prev);
            }}
            userSelect="none"
          >
            <Text textStyle="body.m.medium" color="text.primary">
              Arbeitsblatt aus dieser Lernkarte erstellen
            </Text>
          </HStack>
          <HStack>
            <AiSubscriptionTokenBadge
              amountTokens={
                tokenAccount?.currentTier === AiTierLevel.UNLIMITED ||
                user?.subscription?.status === SubscriptionStatus.VOUCHER
                  ? '∞'
                  : (tokenAccount?.availableMonthlyTokens ?? 0)
              }
              colors={{ background: '#FFEFDA', text: '#8D5610' }}
            />

            <NovaIconButton
              aria-label={isOpen ? 'Sektion einklappen' : 'Sektion ausklappen'}
              variant="primary"
              size="s"
              onClick={() => {
                setUserToggled(true);
                setIsOpen((prev) => !prev);
              }}
              icon={isOpen ? ChevronDownIcon : ChevronRightIcon}
              iconProps={{
                transform: undefined,
              }}
            />
          </HStack>
        </HStack>

        <Collapsible.Root open={isOpen}>
          <Collapsible.Content>
            <VStack align="stretch" gap="m" width="100%">
              <Text textStyle="body.s.regular" color="text.secondary">
                Erstelle ein interaktives Arbeitsblatt aus dieser Lernkarte. Wenn gerade ein
                Arbeitsblatt generiert wird, siehst du hier den Fortschritt.
              </Text>

              {/* Model selector */}
              <Box>
                <Text textStyle="body.xs.medium" color="text.primary" mb="s">
                  Erstellungsart:
                </Text>
                <RadioGroup.Root
                  value={model}
                  onValueChange={(details) => setModel(details.value as WorksheetAiModel)}
                  mb="xs"
                  colorPalette="green"
                  textStyle="body.s.regular"
                >
                  <HStack gap="m">
                    <RadioGroup.Item value={WorksheetAiModel.GENERATIVE_AI}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>KI-Generierung (Standard)</RadioGroup.ItemText>
                    </RadioGroup.Item>
                    <RadioGroup.Item value={WorksheetAiModel.BLOOM_METHODOLOGY}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>Taxonomiegestützte KI-Generierung</RadioGroup.ItemText>
                    </RadioGroup.Item>
                  </HStack>
                </RadioGroup.Root>
              </Box>

              {/* Show only if Bloom methodology is selected */}
              {model === WorksheetAiModel.BLOOM_METHODOLOGY && (
                <>
                  {/* Worksheet difficulty selection */}
                  <Box>
                    <Text textStyle="body.xs.medium" color="text.primary" mb="s">
                      Schwierigkeitsgrad wählen:
                    </Text>
                    <RadioGroup.Root
                      value={difficulty}
                      onValueChange={(details) => setDifficulty(details.value as BloomCategory)}
                      mb="xs"
                      colorPalette="green"
                      textStyle="body.s.regular"
                    >
                      <HStack gap="m">
                        <RadioGroup.Item value={BloomCategory.EASY}>
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText>
                            {toGermanLabel(BloomCategory.EASY)}
                          </RadioGroup.ItemText>
                        </RadioGroup.Item>
                        <RadioGroup.Item value={BloomCategory.MEDIUM}>
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText>
                            {toGermanLabel(BloomCategory.MEDIUM)}
                          </RadioGroup.ItemText>
                        </RadioGroup.Item>
                        <RadioGroup.Item value={BloomCategory.HARD}>
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText>
                            {toGermanLabel(BloomCategory.HARD)}
                          </RadioGroup.ItemText>
                        </RadioGroup.Item>
                      </HStack>
                    </RadioGroup.Root>
                  </Box>

                  {/* Knowledge dimension selection */}
                  <Box>
                    <Text textStyle="body.xs.medium" color="text.primary" mb="s">
                      Wissensdimension(en) wählen:
                    </Text>
                    <CheckboxGroup
                      value={knowledgeDimensions}
                      onValueChange={(details) =>
                        setKnowledgeDimensions(details as KnowledgeDimension[])
                      }
                      colorPalette="green"
                    >
                      <HStack gap="m">
                        <Checkbox.Root value={KnowledgeDimension.FACTUAL_KNOWLEDGE}>
                          <Checkbox.HiddenInput />
                          <Checkbox.Control>
                            <Checkbox.Indicator />
                          </Checkbox.Control>
                          <Checkbox.Label>
                            {toGermanLabel(KnowledgeDimension.FACTUAL_KNOWLEDGE)}
                          </Checkbox.Label>
                        </Checkbox.Root>
                        <Checkbox.Root value={KnowledgeDimension.CONCEPTUAL_KNOWLEDGE}>
                          <Checkbox.HiddenInput />
                          <Checkbox.Control>
                            <Checkbox.Indicator />
                          </Checkbox.Control>
                          <Checkbox.Label>
                            {toGermanLabel(KnowledgeDimension.CONCEPTUAL_KNOWLEDGE)}
                          </Checkbox.Label>
                        </Checkbox.Root>
                        <Checkbox.Root value={KnowledgeDimension.PROCEDURAL_KNOWLEDGE}>
                          <Checkbox.HiddenInput />
                          <Checkbox.Control>
                            <Checkbox.Indicator />
                          </Checkbox.Control>
                          <Checkbox.Label>
                            {toGermanLabel(KnowledgeDimension.PROCEDURAL_KNOWLEDGE)}
                          </Checkbox.Label>
                        </Checkbox.Root>
                      </HStack>
                    </CheckboxGroup>
                  </Box>
                </>
              )}

              {/* Text difficulty selection - available for both models */}
              <Box>
                <Text textStyle="body.xs.medium" color="text.primary" mb="s">
                  Sprachniveau:
                </Text>
                <RadioGroup.Root
                  value={useSimpleLanguage ? 'simple' : 'standard'}
                  onValueChange={(details) => setUseSimpleLanguage(details.value === 'simple')}
                  mb="xs"
                  colorPalette="green"
                  textStyle="body.s.regular"
                >
                  <HStack gap="m">
                    <RadioGroup.Item value="standard">
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>Standardsprache</RadioGroup.ItemText>
                    </RadioGroup.Item>
                    <RadioGroup.Item value="simple">
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>Einfache Sprache (nach DIN 8581-1)</RadioGroup.ItemText>
                    </RadioGroup.Item>
                  </HStack>
                </RadioGroup.Root>
              </Box>

              {tokenAccount?.currentTier !== AiTierLevel.UNLIMITED &&
              user?.subscription?.status !== SubscriptionStatus.VOUCHER &&
              tokenAccount?.availableMonthlyTokens === 0 ? (
                !isLoading && !isPolling && <NoTokensAlert onUpgradeClick={handleUpgradeClick} />
              ) : (
                <HStack gap="m">
                  <Button
                    onClick={handleCreateWorksheet}
                    loading={isLoading || isPolling}
                    disabled={isLoading || isPolling}
                    backgroundColor="background.accent"
                    color="grey.white"
                    borderRadius="m"
                    px="15px"
                    py="xs"
                    _hover={{ bg: 'background.accent.hover' }}
                    _active={{ bg: 'background.accent.active' }}
                    textStyle="body.s.regular"
                    fontWeight="normal"
                    gap="5px"
                  >
                    <NovaIcon icon={AiFileIcon} size="s" aria-hidden />
                    Arbeitsblatt generieren
                  </Button>
                  {(isLoading || isPolling) && (
                    <Text textStyle="body.s.regular" color="text.secondary">
                      Generierung läuft...
                    </Text>
                  )}
                </HStack>
              )}

              {(isLoading || isPolling) && (
                <Box
                  width="100%"
                  borderWidth="1px"
                  borderColor="border.accent.active"
                  borderRadius="md"
                  p="s"
                  bg="background.primary"
                >
                  <Progress.Root
                    value={progress}
                    size="sm"
                    colorPalette="green"
                    borderRadius="md"
                    mb="xs"
                    animated
                  >
                    <Progress.Track>
                      <Progress.Range />
                    </Progress.Track>
                  </Progress.Root>
                  <Text textStyle="body.s.regular" color="text.secondary">
                    {progressMessage} ({progress}%)
                  </Text>
                </Box>
              )}

              {error && (
                <Box
                  p="s"
                  bg="red.50"
                  border="default"
                  borderColor="red.200"
                  borderRadius="md"
                  width="100%"
                >
                  <Text textStyle="body.s.regular" color="red.700">
                    Es ist ein Fehler aufgetreten. Bitte versuche es erneut. Wenn das Problem
                    bestehen bleibt, kontaktiere bitte unser Support-Team.
                  </Text>
                  <Button size="m" variant="destructiveOutline" mt="xs" onClick={reset}>
                    Erneut versuchen
                  </Button>
                </Box>
              )}

              {relatedWorksheets && relatedWorksheets.length > 0 && (
                <Box mt="2xs" pt="2xs">
                  <HStack justify="space-between" mb="xs">
                    <Text textStyle="body.s.medium" color="text.primary">
                      Bereits erstellt für diesen Artikel
                    </Text>
                    <Badge colorScheme="grey">{relatedWorksheets.length}</Badge>
                  </HStack>
                  <VStack align="stretch" gap="xs">
                    {relatedWorksheets.slice(0, 5).map((ws) => (
                      <HStack
                        key={ws.id}
                        justify="space-between"
                        borderWidth="1px"
                        borderColor="border.accent"
                        borderRadius="md"
                        p="xs"
                        bg="background.primary"
                      >
                        <Box>
                          <Text textStyle="body.s.medium" color="text.primary">
                            {ws.title || 'Unbenanntes Arbeitsblatt'}
                          </Text>
                          <Text textStyle="body.xs.medium" color="text.secondary">
                            {ws.configuration && ws.configuration.length > 0
                              ? ws.configuration
                                  .map((item) => {
                                    const label = toGermanLabel(item);
                                    return label ? `#${label}` : null;
                                  })
                                  .filter((item) => item !== null)
                                  .join(', ')
                              : null}
                          </Text>
                          <Text textStyle="body.xs.regular" color="text.secondary">
                            {new Date(ws.createdAt).toLocaleDateString('de-DE')}
                          </Text>
                        </Box>
                        <Button
                          variant="secondaryOutline"
                          p="5px 10px"
                          py="0"
                          size="m"
                          textStyle="body.s.medium"
                          onClick={() => navigate(`${ProtectedRoutes.WORKSHEET_DETAIL}/${ws.id}`)}
                        >
                          Öffnen
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </Collapsible.Content>
        </Collapsible.Root>
      </VStack>
    </Box>
  );
};
